import { Inject, Injectable, inject } from '@angular/core';
import { Nexus } from '@tomandco/nexus-api-types';
import map from 'lodash-es/map';
import { App } from '../app';
import { ISite, ISites, NCore } from '../df-core.interfaces';
import { Acl } from '../service/acl';
import { CookiesService } from '../service/cookies';
import { Events } from '../service/event';
import { Logger } from '../service/logger';
import { Client } from './client';

export interface IMultisiteItem extends Nexus.Lib.General.Store, ISite {
    /**
     * Site identifier
     */
    siteId: string;
    storeViewCode?: string;
    storeCode?: string;
}

export const SET_STORE_EVENT_ID = 'setPreferredStoreId';
export const COOKIE_ID = 'preferredStore';

const MULTISITE_PREFERRED_STORE_KEY = 'multisitePreferredStore';
interface IMultisitePreferredStore {
    preferredStoreId: string;
    createdAt: string;
}
declare module '../app' {
    interface App {
        multisite: MultisiteService;
    }
}

@Injectable({
    providedIn: 'root'
})
export class MultisiteService {
    private logger = inject(Logger);

    /**
     * All MS items
     */
    private _all?: IMultisiteItem[];

    // prepare others
    get all(): IMultisiteItem[] {
        if (!this._all) {
            this._all = this.getAll();
        }
        return this._all;
    }

    protected getAll(): IMultisiteItem[] {
        return map(this.app.dfConfig.ms.sites, (site, siteId) => {
            if (site.acl !== undefined) {
                const aclId = `store.${siteId}`;
                Acl.rules[aclId] = site.acl;
                if (!this.acl.check(aclId)) {
                    return;
                }
            }
            const store = this.app.data.stores.find(s => s.id === site.store);
            if (store) {
                return { ...store, ...site, siteId };
            }
        }).filter(i => !!i) as IMultisiteItem[];
    }

    /**
     * Other MS items
     */
    private _other?: IMultisiteItem[];

    // prepare others
    get other(): IMultisiteItem[] {
        if (!this._other) {
            this._other = this.all.filter(i => i.store !== this.storeId);
        }
        return this._other;
    }

    /**
     * current MS item
     */
    private _current?: IMultisiteItem;

    get current(): IMultisiteItem {
        if (!this._current) {
            // prepare current
            this._current = <IMultisiteItem>this.all.find(i => i.siteId === this.siteId);
            if (!this._current) {
                throw new Error('Fatal error! Current store could not be identified.');
            }
        }
        return this._current;
    }

    /**
     * default MS item
     */
    default?: IMultisiteItem;

    /**
     * Preferred site id
     */
    set preferredStoreId(preferredStoreId: string | undefined) {
        if (this.moduleConfig.multisite.useCookie) {
            if (preferredStoreId) {
                this.cookieService.set(COOKIE_ID, preferredStoreId, this.moduleConfig.multisite.cookieExpire);
            } else {
                this.cookieService.delete(COOKIE_ID);
            }
        } else {
            this.app.globalStorageService.set(MULTISITE_PREFERRED_STORE_KEY, {
                preferredStoreId,
                createdAt: new Date().toISOString()
            });
        }
    }

    get preferredStoreId(): string | undefined {
        if (this.moduleConfig.multisite.useCookie) {
            return this.cookieService.get(COOKIE_ID);
        }
        const storage = this.app.globalStorageService.get(MULTISITE_PREFERRED_STORE_KEY) as string | IMultisitePreferredStore | undefined;
        if (typeof storage === 'string' && !this.moduleConfig.multisite.cookieExpire) {
            return storage;
        }
        if (typeof storage === 'object' && 'preferredStoreId' in storage && 'createdAt' in storage) {
            const expieryDate = storage.createdAt ? new Date(storage.createdAt) : new Date();
            expieryDate.setDate(expieryDate.getDate() + (this.moduleConfig.multisite.cookieExpire || 30));
            if (expieryDate > new Date()) {
                return storage.preferredStoreId;
            }
        }
        return undefined;
    }

    /**
     * if '?noredir=1' param was set.
     */
    noRedirect?: boolean;

    constructor(
        private events: Events,
        protected app: App,
        private client: Client,
        private cookieService: CookiesService,
        private acl: Acl,
        @Inject(NCore.OPTIONS) private moduleConfig: NCore.IModuleOptions
    ) {
        this.init();

        app.multisite = this;

        // prepare current
        // this.default = <IMultisiteItem>(this.all.find(i => i.default) || this.all[0]);
    }

    protected init() {
        /**
         * noredir=1 query param prevents auto-redirect to a diffrent store in case prefferedStore cookie is present,
         * (redirects are handled by nginx, cannot use activatedRoute in this place because it sets query params
         * way too late for it to work propery in most situations so we need to get them directly from window.location.search)
         */
        if (typeof window !== 'undefined') {
            const noredirQueryParam = new URLSearchParams(window.location.search).get('noredir');
            if (noredirQueryParam === '1') {
                this.noRedirect = true;
            }
        }
        // reset on cms switch
        this.client.isCms$.subscribe(() => {
            this._all = undefined;
            this._other = undefined;
            this._current = undefined;
        });
    }

    /**
     * Gets current store id
     */
    get storeId(): number {
        const ms = this.app.dfConfig.ms;
        return ms.sites[ms.siteId].store;
    }

    /**
     * Gets current store id
     */
    get siteId(): keyof ISites {
        return this.app.dfConfig.ms.siteId;
    }

    /**
     * Base is always with both side slashes and is always defined
     */
    get base(): string {
        return this.app.dfConfig.ms.sites[this.app.dfConfig.ms.siteId].base!;
    }

    /**
     * Is right-to-left typing
     */
    get isRtl(): boolean {
        return !!this.app.dfConfig.ms.sites[this.app.dfConfig.ms.siteId].rtl;
    }

    /**
     * Saves default store prederence
     */
    setPreferredStore(storeId?: string | number | IMultisiteItem) {
        const site = this.toMultisiteItem(storeId);
        if (site) {
            this.preferredStoreId = site.siteId;
            this.events.broadcast(SET_STORE_EVENT_ID);
        }
    }

    /**
     * Checks if user has a valid preferred store
     */
    hasPreferredStore(): boolean {
        const siteId = this.preferredStoreId;
        return !!(siteId && this.toMultisiteItem(siteId));
    }

    /**
     * Helper method to normalize any input to ms item
     */
    toMultisiteItem(query?: string | number | IMultisiteItem): IMultisiteItem | undefined {
        switch (typeof query) {
            case 'number':
                return this.all.find(i => i.store === query);
            case 'string':
                return this.all.find(i => i.siteId === query);
            case 'object':
                return this.all.find(i => i.id === query.id);
        }
        return undefined;
    }

    geoMatch(cb: (store: IMultisiteItem | undefined) => void) {
        this.app.customer.model.load().then(a => {
            const geo = a.data.geo;
            const store = geo
                ? this.all.find(item => (item.countries ? item.countries.includes(geo) : item.country === geo)) ||
                  this.all.find(item => item.countries && item.countries.includes('default'))
                : undefined;
            cb(store);
        }).catch(e => this.logger.error(e));
    }
}
